import type { Leave } from '@lms/shared-types'
import { LEAVE_TYPE_LABEL } from './leave-type-meta'

const CSV_HEADER = [
  'Leave ID',
  'Type',
  'Start date',
  'End date',
  'Days',
  'Status',
  'Submitted',
  'Reason',
  'Decided by',
  'Decided at',
  'Decision reason',
] as const

/** Calendar days inclusive between start/end, minus 0.5 per half-day slot.
 *  Migration 0057's halfDays[] array is authoritative — when it exists the
 *  legacy halfStart/halfEnd fields are ignored even if present on the same
 *  row (handles in-flight back-fill correctly per Kavi round-1 P2 #18). */
function daysFor(leave: Leave): number {
  const start = new Date(leave.startDate)
  const end = new Date(leave.endDate)
  const inclusive = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1)
  if (leave.halfDays !== undefined) {
    return inclusive - 0.5 * leave.halfDays.length
  }
  const legacyHalves = (leave.halfStart ? 0.5 : 0) + (leave.halfEnd ? 0.5 : 0)
  return inclusive - legacyHalves
}

/** RFC 4180 minimal CSV escape — wraps a field in quotes if it contains a
 *  comma, quote, or newline; doubles any embedded quotes. */
export function csvField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  const s = String(value)
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

/** Convert leaves to a CSV string suitable for a download. */
export function leavesToCsv(leaves: ReadonlyArray<Leave>): string {
  const rows = [CSV_HEADER.join(',')]
  for (const l of leaves) {
    rows.push(
      [
        csvField(l.id),
        csvField(LEAVE_TYPE_LABEL[l.type] ?? l.type),
        csvField(l.startDate),
        csvField(l.endDate),
        csvField(daysFor(l)),
        csvField(l.status),
        csvField(l.createdAt),
        csvField(l.reason),
        csvField(l.decidedById),
        csvField(l.decidedAt),
        csvField(l.rejectReason),
      ].join(','),
    )
  }
  return `${rows.join('\n')}\n`
}

/** Trigger a browser download with the given CSV content. */
export function downloadCsv(filename: string, content: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}
